import { useEffect, useState } from "react";
import supabase from "../../api/supabase"; // Adjust the path
import { RealtimeChannel } from '@supabase/supabase-js';

interface UserProfile {
  empUserId: string;
  id: string;
  empAvatar: string;
  empName: string;
  empRole: string[];
  empEmail: string;
}

const LOCAL_STORAGE_KEY = "userProfile"; // Key to store user profile
const EXPIRY_KEY = "userProfileExpiry"; // Key to store expiry time
const EXPIRY_DURATION = 60 * 60 * 1000; // 1 hour expiration duration

const useCurrentUser = () => {
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // Step 1: Check if there's cached data in localStorage
        const cachedProfile = localStorage.getItem(LOCAL_STORAGE_KEY);
        const profileExpiry = localStorage.getItem(EXPIRY_KEY);
        const isExpired = !profileExpiry || Date.now() > Number(profileExpiry);

        // Step 2: If cached data exists and is not expired, use it
        if (cachedProfile && !isExpired) {
          const userProfile = JSON.parse(cachedProfile) as UserProfile;
          setCurrentUser(userProfile);
          return; // Skip fetching the profile again
        }

        // Step 3: If no cached data or expired, check for active session
        const { data: sessionData, error: sessionError } =
          await supabase.auth.getSession();

        if (sessionError || !sessionData.session) {
          console.error("No active session found.");
          setCurrentUser(null);
          return;
        }

        // Step 4: Fetch authenticated user data
        const { data: userData, error: userError } =
          await supabase.auth.getUser();

        if (userError || !userData?.user) {
          console.error("Error fetching authenticated user:", userError);
          setCurrentUser(null);
          return;
        }

        // Step 5: Fetch user profile based on authenticated user
        const { data: profile, error: profileError } = await supabase
          .from("employees")
          .select("empUserId, id, empAvatar, empName, empRole, empEmail")
          .eq("empUserId", userData.user.id)
          .single();

        if (profileError || !profile) {
          console.error("Error fetching user profile:", profileError);
          setCurrentUser(null);
          return;
        }

        // Step 6: Save fetched profile to localStorage and update state
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(profile));
        localStorage.setItem(
          EXPIRY_KEY,
          (Date.now() + EXPIRY_DURATION).toString()
        );
        setCurrentUser(profile as UserProfile);
      } catch (error) {
        console.error("Unexpected error:", error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []); // Initial profile fetch runs only once

  // Separate useEffect for real-time subscription
  useEffect(() => {
    let subscription: RealtimeChannel;
    
    if (currentUser?.id) {
      subscription = supabase
        .channel("employees")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "employees",
            filter: `id=eq.${currentUser.id}`,
          },
          (payload) => {
            const newProfile = payload.new;
            const hasChanges = JSON.stringify(currentUser) !== JSON.stringify(newProfile);
            
            if (hasChanges) {
              setCurrentUser(newProfile as UserProfile);
              localStorage.setItem(
                LOCAL_STORAGE_KEY,
                JSON.stringify(newProfile)
              );
            }
          }
        )
        .subscribe();
    }

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [currentUser]); // Subscription depends on currentUser

  // Function to clear user data from localStorage and state
  const clearUserProfile = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    localStorage.removeItem(EXPIRY_KEY); // Clear expiry time too
    setCurrentUser(null);
  };

  return { currentUser, loading, clearUserProfile };
};

export default useCurrentUser;
